import MathText from './MathText';
import MathAnswerInput from './MathAnswerInput';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { burstConfetti } from '../lib/celebrate';

const API_URL = 'https://api.brainteaserday.com';

function initialsOf(name) {
  try {
    const parts = String(name || '').trim().split(/[\s._-]+/).filter(Boolean);
    if (parts.length === 0) return '?';
    return ((parts[0][0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
  } catch { return '?'; }
}

/* ── helpers ─────────────────────────────────────────────────── */
function fmtTime(s) {
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

const LEVEL_LABELS = { easy: 'Facile', med: 'Moyen', hard: 'Difficile' };
const LEVEL_COLORS = { easy: '#10b981', med: '#f59e0b', hard: '#ef4444' };

const RACE_ACHIEVEMENT_META = {
  race_first:    { icon: '🏁', title: 'Premier départ' },
  race_score_10: { icon: '⚡', title: 'Décollage' },
  race_score_25: { icon: '🔥', title: 'En feu' },
  race_hard:     { icon: '💪', title: 'Intrépide' },
  race_perfect:  { icon: '🎯', title: 'Parfait !' },
};

const prefersReduced = () => { try { return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches; } catch { return false; } };

/* ── Leaderboard Course ───────────────────────────────────────── */
function RaceLeaderboard({ level, duration, currentUserId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,setError]=useState(false),[retry,setRetry]=useState(0);

useEffect(() => {
  let mounted = true;
  setLoading(true); setError(false);

  (async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/leaderboard/race?level=${encodeURIComponent(level)}&duration=${duration}`
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || 'Classement indisponible');
      }

      if (mounted) {
        setRows(Array.isArray(data.rows) ? data.rows : []);
      }
    } catch (error) {
      console.error(error);

      if (mounted) {
        setRows([]);setError(true);
      }
    } finally {
      if (mounted) {
        setLoading(false);
      }
    }
  })();

  return () => {
    mounted = false;
  };
}, [level, duration, retry]);


  if (loading) return <div style={{ fontSize: 13, color: 'var(--muted)', textAlign: 'center', padding: 12 }}>Chargement…</div>;
  if(error)return <div role="alert">Classement temporairement indisponible. <button className="btn" onClick={()=>setRetry(v=>v+1)}>Réessayer</button></div>;
  if (rows.length === 0) return <div style={{ fontSize: 13, color: 'var(--muted)', textAlign: 'center', padding: 12 }}>Aucun score pour cette configuration</div>;

  return (
    <div style={{ display: 'grid', gap: 4 }}>
      {rows.map((r, idx) => {
        const isMe = r.user_id === currentUserId;
        return (
          <div key={r.user_id} style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 10,
            background: isMe ? 'var(--primary-soft)' : idx % 2 === 0 ? 'var(--surface-subtle)' : 'transparent',
            border: isMe ? '1px solid rgba(99,102,241,0.3)' : '1px solid transparent',
          }}>
            <span style={{
              width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 800, flexShrink: 0,
              background: idx === 0 ? '#f59e0b' : idx === 1 ? '#94a3b8' : idx === 2 ? '#b45309' : 'var(--surface-subtle)',
              color: idx < 3 ? '#fff' : 'var(--muted)',
            }}>{idx + 1}</span>
            <span style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--primary-soft)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
              {initialsOf(r.username)}
            </span>
            <span style={{ flex: 1, fontSize: 13, fontWeight: isMe ? 700 : 500, color: isMe ? 'var(--primary)' : 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {r.username || '—'}{isMe ? ' (moi)' : ''}
            </span>
            <span style={{ fontWeight: 800, fontSize: 15, color: idx === 0 ? '#f59e0b' : 'var(--text)' }}>{r.score}</span>
            <span style={{ fontSize: 11, color: 'var(--muted)', minWidth: 32, textAlign: 'right' }}>{r.accuracy}%</span>
          </div>
        );
      })}
    </div>
  );
}

/* ── End Screen ──────────────────────────────────────────────── */
function EndScreen({ score, attempts, maxCombo, duration, level, personalBest, newAchievements, onRestart, userId, ranked }) {
  const accuracy = attempts > 0 ? Math.round((score / attempts) * 100) : 0;
  const isNewRecord = ranked && (personalBest === null || score > personalBest);
  const levelColor = LEVEL_COLORS[level];

  return (
    <div className="race-end-screen fade-in">
      <p className="race-verification">{ranked ? "✓ Score vérifié et enregistré dans le classement" : "Partie interrompue · score non classé"}</p>{/* Score header */}
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={{ fontSize: 64, marginBottom: 8 }}>
          {isNewRecord ? '🏆' : score >= 20 ? '🔥' : score >= 10 ? '⚡' : '🏁'}
        </div>
        <div style={{ fontSize: 48, fontWeight: 900, lineHeight: 1, color: isNewRecord ? '#f59e0b' : 'var(--text)' }}>
          {score}
        </div>
        <div style={{ fontSize: 14, color: 'var(--muted)', marginTop: 4 }}>points</div>
        {isNewRecord && (
          <div style={{
            display: 'inline-block', marginTop: 10,
            background: 'linear-gradient(135deg, #f59e0b, #fbbf24)',
            color: '#fff', fontWeight: 800, fontSize: 13,
            padding: '4px 14px', borderRadius: 20,
            animation: 'race-record-shine 1.5s ease infinite',
          }}>
            ⭐ NOUVEAU RECORD !
          </div>
        )}
        {!isNewRecord && personalBest !== null && (
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 8 }}>
            Record personnel : {personalBest} pts
          </div>
        )}
      </div>

      {/* Stats grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 20 }}>
        {[
          { label: 'Précision', value: `${accuracy}%`, icon: '🎯' },
          { label: 'Meilleure série', value: `×${maxCombo}`, icon: '🔥' },
          { label: `Durée`, value: fmtTime(duration), icon: '⏱' },
        ].map((s, i) => (
          <div key={i} className="card" style={{ padding: '14px 8px', textAlign: 'center' }}>
            <div style={{ fontSize: 20, marginBottom: 4 }}>{s.icon}</div>
            <div style={{ fontWeight: 800, fontSize: 18, lineHeight: 1 }}>{s.value}</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Achievements earned */}
      {newAchievements.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 8 }}>
            Succès débloqués 🏅
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            {newAchievements.map((a, i) => {
              const meta = RACE_ACHIEVEMENT_META[a.achievement_key] || {};
              return (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  background: 'var(--primary-soft)', border: '1px solid rgba(99,102,241,0.2)',
                  borderRadius: 10, padding: '10px 14px',
                  animation: `achievement-slide-in 400ms ease ${i * 150}ms both`,
                }}>
                  <span style={{ fontSize: 22 }}>{meta.icon || '🏅'}</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{a.achievement_title || meta.title}</div>
                    <div style={{ fontSize: 12, color: 'var(--primary)' }}>Nouveau succès débloqué !</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Level badge */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
        <span style={{
          background: levelColor + '22', color: levelColor,
          border: `1px solid ${levelColor}44`,
          fontWeight: 700, fontSize: 13, padding: '4px 14px', borderRadius: 20,
        }}>
          {LEVEL_LABELS[level]} · {fmtTime(duration)}
        </span>
      </div>

      <button className="btn btn-primary" onClick={onRestart} style={{ width: '100%', fontSize: 16, padding: '14px', marginBottom: 24 }}>
        🔄 Rejouer
      </button>

      {/* Classement Course */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--card-border)', fontSize: 14, fontWeight: 700 }}>
          🏆 Classement — {LEVEL_LABELS[level]} · {fmtTime(duration)}
        </div>
        <div style={{ padding: 10 }}>
          <RaceLeaderboard level={level} duration={duration} currentUserId={userId} />
        </div>
      </div>
    </div>
  );
}

/* ── Main RaceGame ───────────────────────────────────────────── */
export default function RaceGame({ session }) {
  const [running,setRunning]=useState(false), [duration,setDuration]=useState(60), [timeLeft,setTimeLeft]=useState(60);
  const [level,setLevel]=useState('med'), [eq,setEq]=useState({text:'Prêt à relever le défi ?'}), [input,setInput]=useState('');
  const [score,setScore]=useState(0),[attempts,setAttempts]=useState(0),[combo,setCombo]=useState(0),[maxCombo,setMaxCombo]=useState(0);
  const [history,setHistory]=useState([]),[flash,setFlash]=useState(null),[personalBest,setPersonalBest]=useState(null);
  const [showEnd,setShowEnd]=useState(false),[newAchievements,setNewAchievements]=useState([]),[saving,setSaving]=useState(false);
  const [busy,setBusy]=useState(false),[error,setError]=useState(''),[ranked,setRanked]=useState(false);
  const inputRef=useRef(null), runRef=useRef(null), deadlineRef=useRef(0), pendingRef=useRef(null), busyRef=useRef(false),finishRef=useRef(false);
  const answerPromise=useRef(null), mounted=useRef(true);
  useEffect(()=>{mounted.current=true;return()=>{mounted.current=false;};},[]);
  const api=useCallback(async(path,body)=>{
    const controller=new AbortController(), timer=setTimeout(()=>controller.abort(),12000);
    try {
      const response=await fetch(API_URL+'/api/race'+path,{method:'POST',signal:controller.signal,headers:{'Content-Type':'application/json',Authorization:'Bearer '+localStorage.getItem('auth_token')},body:JSON.stringify(body)});
      const data=await response.json();
      if(!response.ok) throw Object.assign(new Error(data.error || 'Course indisponible.'),{status:response.status});
      return data;
    } catch(e) {if(e.name==='AbortError') throw new Error('Connexion trop lente. Réessaie.');throw e;}
    finally{clearTimeout(timer);}
  },[]);
  const loadPB=useCallback(async()=>{
    try{
      const res=await fetch(API_URL+'/api/me/race-best?level='+level+'&duration='+duration,{headers:{Authorization:'Bearer '+localStorage.getItem('auth_token')}});
      const data=await res.json(); if(res.ok&&mounted.current)setPersonalBest(data.best_score??null);
    }catch{if(mounted.current)setPersonalBest(null);}
  },[level,duration]);
  useEffect(()=>{loadPB();},[loadPB]);
  const update=data=>{
    setScore(data.score);setAttempts(data.attempts);setCombo(data.combo);setMaxCombo(data.maxCombo);
    if(data.question)setEq(data.question);
  };
  async function start(){
    if(busyRef.current||finishRef.current)return;
    busyRef.current=true;setBusy(true);setError('');
    try{
      const data=await api('/start',{level,duration});if(!mounted.current)return;
      runRef.current=data.id;deadlineRef.current=performance.now()+data.remainingMs;pendingRef.current=null;
      update(data);setInput('');setHistory([]);setNewAchievements([]);setRanked(false);setTimeLeft(Math.ceil(data.remainingMs/1000));setShowEnd(false);setRunning(true);
    }catch(e){setError(e.message);}finally{busyRef.current=false;setBusy(false);}
  }
  const finish=useCallback(async(abort=false)=>{
    if(finishRef.current||!runRef.current)return;
    finishRef.current=true;setSaving(true);setError('');
    try{
      await answerPromise.current;
      let data;
      try{data=await api('/'+runRef.current+'/finish',{abort});}
      catch(e){if(e.status!==409)throw e; await new Promise(resolve=>setTimeout(resolve,500));data=await api('/'+runRef.current+'/finish',{abort});}
      if(!mounted.current)return;
      update(data);setRanked(data.ranked);setNewAchievements(data.achievements||[]);setRunning(false);setShowEnd(true);
    }catch(e){if(mounted.current){setError(e.message);setRunning(false);}}
    finally{finishRef.current=false;if(mounted.current)setSaving(false);}
  },[api]);
  useEffect(()=>{
    if(!running)return;
    const tick=()=>{const seconds=Math.max(0,Math.ceil((deadlineRef.current-performance.now())/1000));setTimeLeft(seconds);if(!seconds)finish();};
    const timer=setInterval(tick,100);return()=>clearInterval(timer);
  },[running,finish]);
  useEffect(()=>{if(running&&!busy)inputRef.current?.focus();},[running,busy]);
  function submit(e){
    e.preventDefault();if(!running||busyRef.current||finishRef.current||timeLeft<=0||!input.trim())return;
    const request=pendingRef.current||{requestId:crypto.randomUUID(),questionId:eq.id,guess:input};pendingRef.current=request;
    busyRef.current=true;setBusy(true);setError('');
    answerPromise.current=(async()=>{
      try{
        const data=await api('/'+runRef.current+'/answer',request);if(!mounted.current)return;
        update(data);pendingRef.current=null;setInput('');
        setHistory(h=>[{q:data.previous.text,a:data.previous.answer,g:request.guess,ok:data.correct},...h.slice(0,6)]);
        setFlash(data.correct?'correct':'wrong');setTimeout(()=>{if(mounted.current)setFlash(null);},350);
        if(data.correct&&!prefersReduced())burstConfetti({count:20});
      }catch(e){if(mounted.current){setError(e.message);if(e.status){pendingRef.current=null;}if(e.status===410)setTimeLeft(0);}}
      finally{busyRef.current=false;if(mounted.current)setBusy(false);}
    })();
  }

  const pct = duration > 0 ? (timeLeft / duration) * 100 : 0;
  const isUrgent = pct <= 16; // last ~10s for 60s
  const levelColor = LEVEL_COLORS[level];

  if (showEnd) {
    return (
      <div className="page-container fade-in">
        {error && <p role="alert">{error}</p>}
        <EndScreen
          score={score}
          attempts={attempts}
          maxCombo={maxCombo}
          duration={duration}
          level={level}
          personalBest={personalBest}
          newAchievements={newAchievements}
          onRestart={start}
          userId={session?.user?.id}
          ranked={ranked}
        />
      </div>
    );
  }

  return (
    <div className="page-container fade-in">
      <h2 className="page-title">🏁 Mode Course</h2>
      <p className="race-verification">Chaque réponse est vérifiée. Seules les parties terminées alimentent le classement.</p>
      {error && <div className="card section" role="alert"><p>{error}</p>{!running && runRef.current && !showEnd && <button className="btn" disabled={saving} onClick={()=>finish()}>Réessayer l’enregistrement</button>}</div>}

      {/* Config bar (hidden when running) */}
      {!running && (
        <div className="card section" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
              {/* Difficulty */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 }}>Difficulté</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {['easy', 'med', 'hard'].map(l => (
                    <button key={l} onClick={() => setLevel(l)} style={{
                      padding: '8px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: '2px solid',
                      background: level === l ? LEVEL_COLORS[l] + '22' : 'transparent',
                      borderColor: level === l ? LEVEL_COLORS[l] : 'var(--card-border)',
                      color: level === l ? LEVEL_COLORS[l] : 'var(--muted)',
                      minHeight: 40,
                    }}>
                      {LEVEL_LABELS[l]}
                    </button>
                  ))}
                </div>
              </div>
              {/* Duration */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 }}>Durée</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {[30, 60, 120].map(d => (
                    <button key={d} onClick={() => setDuration(d)} style={{
                      padding: '8px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: '2px solid',
                      background: duration === d ? '#6366f122' : 'transparent',
                      borderColor: duration === d ? '#6366f1' : 'var(--card-border)',
                      color: duration === d ? '#6366f1' : 'var(--muted)',
                      minHeight: 40,
                    }}>
                      {d}s
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {/* Start */}
            <div>
              {personalBest !== null && (
                <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
                  Record : <strong style={{ color: 'var(--text)' }}>{personalBest} pts</strong>
                </div>
              )}
              <button className="btn btn-primary" onClick={start} disabled={busy||saving} style={{ fontSize: 16, padding: '12px 28px', width: '100%' }}>
                Démarrer 🚀
              </button>
            </div>
          </div>
        </div>
      )}

      {running && (
        <>
          {/* Timer bar */}
          <div style={{ marginBottom: 12 }}>
            <div style={{
              height: 8, borderRadius: 4, background: 'var(--surface-subtle)',
              overflow: 'hidden',
            }}>
              <div style={{
                height: '100%', borderRadius: 4,
                width: `${pct}%`,
                background: isUrgent
                  ? 'linear-gradient(90deg, #ef4444, #f97316)'
                  : `linear-gradient(90deg, ${levelColor}, ${levelColor}cc)`,
                transition: 'width 1s linear, background 0.5s ease',
                animation: isUrgent ? 'race-urgency-pulse 0.6s ease infinite' : 'none',
              }} />
            </div>
          </div>

          {/* HUD */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'nowrap' }}>
            {/* Timer */}
            <div style={{
              fontVariantNumeric: 'tabular-nums',
              fontSize: 'clamp(22px, 8vw, 32px)', fontWeight: 900, lineHeight: 1,
              color: isUrgent ? '#ef4444' : 'var(--text)',
              animation: isUrgent ? 'race-urgency-pulse 0.6s ease infinite' : 'none',
              minWidth: 'clamp(52px, 15vw, 70px)',
            }}>
              {fmtTime(timeLeft)}
            </div>

            <div style={{ flex: 1 }} />

            {/* Score */}
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 'clamp(26px, 9vw, 36px)', fontWeight: 900, lineHeight: 1, color: levelColor }}>{score}</div>
              <div style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase' }}>Score</div>
            </div>

            {/* Combo badge */}
            {combo >= 3 && (
              <div style={{
                background: combo >= 10 ? 'linear-gradient(135deg, #f59e0b, #ef4444)'
                  : combo >= 5 ? 'linear-gradient(135deg, #6366f1, #a78bfa)'
                  : 'linear-gradient(135deg, #10b981, #06b6d4)',
                color: '#fff', borderRadius: 12,
                padding: '5px 10px', textAlign: 'center',
                animation: 'combo-pop 0.2s ease',
                minWidth: 50,
              }}>
                <div style={{ fontSize: 'clamp(13px, 4vw, 16px)', fontWeight: 900, lineHeight: 1 }}>×{combo}</div>
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.5px', opacity: 0.9 }}>COMBO</div>
              </div>
            )}

            {/* Stop */}
            <button className="btn" disabled={saving} onClick={() => finish(true)} style={{ fontSize: 12, padding: '8px 12px', minWidth: 56 }}>
              ✕ Stop
            </button>
          </div>

          {/* Personal best tracker */}
          {personalBest !== null && (
            <div style={{ textAlign: 'center', marginBottom: 10, fontSize: 12, color: 'var(--muted)' }}>
              Record : <strong style={{ color: score >= personalBest ? '#f59e0b' : 'var(--text)' }}>{personalBest}</strong>
              {score >= personalBest && score > 0 && (
                <span style={{ color: '#10b981', fontWeight: 700, marginLeft: 6 }}>↑ Battu !</span>
              )}
            </div>
          )}
        </>
      )}

      {/* Equation card */}
      <div
        className={`card section race-equation-card ${flash === 'correct' ? 'race-flash-correct' : flash === 'wrong' ? 'race-flash-wrong' : ''}`}
        style={{ marginBottom: 16, position: 'relative', overflow: 'hidden' }}
      >
        {/* Level badge top-right */}
        <div style={{ position: 'absolute', top: 12, right: 12, background: levelColor + '22', color: levelColor, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 12 }}>
          {LEVEL_LABELS[level]}
        </div>

        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 12 }}>
          Résous
        </div>
        <div className="race-equation" style={{ fontSize: 'clamp(2rem, 8vw, 3.5rem)', marginBottom: 0 }}>
          {eq.id ? <MathText>{`\\(${eq.text.replaceAll('×', '\\times ').replaceAll('÷', '\\div ')} = ?\\)`}</MathText> : <span style={{fontSize:22}}>Prêt à relever le défi ?</span>}
        </div>

        <form onSubmit={submit} style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 20, maxWidth: 300, margin: '20px auto 0' }}>
          <MathAnswerInput
            aria-label="Réponse à la course"
            ref={inputRef}
            type="number"
            inputMode="numeric"
            value={input}
            onChange={e => {if(!pendingRef.current)setInput(e.target.value);}}
            placeholder="?"
            disabled={!running || timeLeft <= 0 || busy || saving}
            className="input"
            style={{ fontSize: 22, textAlign: 'center', fontWeight: 700, flex: 1, minHeight: 52 }}
            autoComplete="off"
          />
          <button type="submit" className="btn btn-primary" disabled={!running || timeLeft <= 0 || busy || saving} style={{ padding: '10px 20px', fontSize: 18, minHeight: 52, minWidth: 52 }}>
            {busy ? "…" : pendingRef.current ? "↻" : "✓"}
          </button>
        </form>
      </div>

      {/* Saving indicator */}
      {saving && (
        <div style={{ textAlign: 'center', fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>
          Sauvegarde en cours...
        </div>
      )}

      {/* Recent history feed */}
      {history.length > 0 && (
        <div className="card" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--card-border)', fontSize: 13, fontWeight: 600 }}>
            Historique récent
          </div>
          <div style={{ padding: 8, display: 'grid', gap: 3 }}>
            {history.map((h, i) => (
              <div key={i} className="history-item" style={{ padding: '5px 10px', opacity: 1 - i * 0.12 }}>
                <span style={{
                  width: 20, height: 20, borderRadius: '50%', display: 'inline-flex',
                  alignItems: 'center', justifyContent: 'center', fontSize: 11,
                  background: h.ok ? '#10b98122' : '#ef444422',
                  color: h.ok ? '#10b981' : '#ef4444', flexShrink: 0,
                }}>
                  {h.ok ? '✓' : '✗'}
                </span>
                <code style={{ fontSize: 13, flex: 1 }}>{h.q}</code>
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>= {h.a}</span>
                {!h.ok && <span style={{ fontSize: 12, color: '#ef4444', marginLeft: 6 }}>({h.g})</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pre-start placeholder */}
      {!running && history.length === 0 && !showEnd && (
        <div className="card section" style={{ textAlign: 'center', padding: '32px 20px' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>⚡</div>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>Prêt à jouer ?</div>
          <div style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.6 }}>
            Choisis ta difficulté et ta durée, puis clique sur Démarrer.<br />
            Résous un maximum d'opérations avant la fin du temps !
          </div>
        </div>
      )}
    </div>
  );
}
